import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, IconButton, Box, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useNavigate } from 'react-router-dom';

// Fix for default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Create custom orange marker icon using SVG
const orangeMarkerIcon = L.divIcon({
  html: `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="36" height="36">
      <path fill="#FF8C00" d="M12 0C7.802 0 4 3.403 4 7.602C4 11.8 12 24 12 24S20 11.8 20 7.602C20 3.403 16.199 0 12 0ZM12 11C10.343 11 9 9.657 9 8C9 6.343 10.343 5 12 5C13.657 5 15 6.343 15 8C15 9.657 13.657 11 12 11Z"/>
      <circle fill="white" cx="12" cy="8" r="3"/>
    </svg>
  `,
  className: "custom-marker",
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -36]
});

// Create a wrapper component that initializes the map using useEffect
const LeafletMap = ({ center, zoom, locations }) => {
  const navigate = useNavigate();

  useEffect(() => {
    // Create map instance
    const map = L.map('map').setView(center, zoom);

    // Add tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(map);

    // Add markers
    locations.forEach(distributor => {
      const marker = L.marker([distributor.lat, distributor.lng], { 
        icon: orangeMarkerIcon,
        title: `${distributor.name}\n${distributor.city}, ${distributor.province}` // Shows location on hover
      }).addTo(map);

      // Create popup content with a clickable link
      const popupContent = document.createElement('div');
      popupContent.innerHTML = `
        <div style="min-width: 200px;">
          <b>${distributor.name}</b><br>
          ${distributor.address}<br>
          ${distributor.city}, ${distributor.province}<br>
          <a href="/distributor/${distributor._id}" style="color: #FF8C00; margin-top: 8px; display: inline-block;">
            Ver productos →
          </a>
        </div>
      `;

      // Add click handler to the link
      const popup = L.popup().setContent(popupContent);
      marker.bindPopup(popup);

      // Add click handler to the marker itself
      marker.on('click', () => {
        setTimeout(() => {
          const link = document.querySelector(`a[href="/distributor/${distributor._id}"]`);
          if (link) {
            link.addEventListener('click', (e) => {
              e.preventDefault();
              navigate(`/distributor/${distributor._id}`);
            });
          }
        }, 100);
      });
    });

    // Cleanup
    return () => {
      map.remove();
    };
  }, [center, zoom, locations, navigate]);

  return <div id="map" style={{ height: '100%', width: '100%' }} />;
};

const MapDialog = ({ open, onClose, distributors, province, city,category }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapCenter, setMapCenter] = useState(null);
  const [zoom, setZoom] = useState(13);

  useEffect(() => {
    async function geocodeAddresses() {
      if (!open || !distributors.length) return;
      
      setLoading(true);
      try {
        // Geocode each distributor address
        const promises = distributors.map(async (distributor) => {
          const fullAddress = `${distributor.address}, ${distributor.city || city}, ${distributor.province || province}, Argentina`;
          const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fullAddress)}`);
          const data = await response.json();
          
          if (data.length > 0) {
            return {
              ...distributor,
              lat: parseFloat(data[0].lat),
              lng: parseFloat(data[0].lon)
            };
          }
          return null;
        });

        const results = await Promise.all(promises);
        const validLocations = results.filter(Boolean);
        
        if (validLocations.length > 0) {
          // Center the map on the first distributor's location
          setMapCenter([validLocations[0].lat, validLocations[0].lng]);
          setZoom(city ? 13 : 10);
        }
        
        setLocations(validLocations);
      } catch (error) {
        console.error('Error geocoding addresses:', error);
      }
      setLoading(false);
    }

    geocodeAddresses();
  }, [open, distributors, province, city]);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          Distribuidores{category ? ` de ${category}` : ''} en {city ? `${city}, ${province}` : province}
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <Box sx={{ height: '70vh', m: 2, position: 'relative' }}>
        {loading ? (
          <Box sx={{ 
            position: 'absolute', 
            top: '50%', 
            left: '50%', 
            transform: 'translate(-50%, -50%)'
          }}>
            <CircularProgress />
          </Box>
        ) : mapCenter ? (
          <LeafletMap 
            center={mapCenter}
            zoom={zoom}
            locations={locations}
          />
        ) : null}
      </Box>
    </Dialog>
  );
};

export default MapDialog; 